import { PagePaths } from '../../constants';
import { isSavedQueryVariableMetric } from '../data-type';
import type { EntityType } from './types/dimension';
import type { MetricValues } from './types/metric';

/** The URL segment of a page an entity's analytics can carry. */
const PAGE_DOMAINS = [
  'compositions',
  'diversity',
  'subsidiaries',
  'transitions',
  'postings',
  'pay',
  'sentiment',
  'market-conditions',
  'screener',
] as const;

export type PageDomain = (typeof PAGE_DOMAINS)[number];

const isPageDomain = (segment: string): segment is PageDomain =>
  (PAGE_DOMAINS as readonly string[]).includes(segment);

/**
 * The pages the product routes for an entity, read off `PagePaths`: every
 * `entity/segment` route whose segment is a page. The `*_LEGACY` aliases and
 * the `summary` redirects have segments outside `PAGE_DOMAINS`, so they do not
 * count.
 */
const routedPageDomains = (entity: EntityType): readonly PageDomain[] =>
  Object.values(PagePaths).flatMap((path) => {
    const [pathEntity, segment, ...rest] = path.split('/');
    return pathEntity === entity &&
      rest.length === 0 &&
      segment !== undefined &&
      isPageDomain(segment)
      ? [segment]
      : [];
  });

/**
 * The pages the product has for each entity. Listed per entity so a new
 * entity fails to compile until it is mapped. The dashboard's tab strip is
 * pinned to this record (`entity-page-domains.spec`) and the assistant's page
 * catalog is checked against it (`page-domains.spec`), so chart capability
 * follows the product's routes and never the assistant's routing.
 */
export const ENTITY_PAGE_DOMAINS: Record<EntityType, readonly PageDomain[]> = {
  company: routedPageDomains('company'),
  industry: routedPageDomains('industry'),
  geography: routedPageDomains('geography'),
  role: routedPageDomains('role'),
  skill: routedPageDomains('skill'),
  activity: routedPageDomains('activity'),
};

/**
 * The page each metric is shown on, or `null` for a figure no page charts.
 * Total over `MetricValues`, so a new metric does not compile until it is
 * classified; `page-domains.spec` pins the picker groups to it.
 * `metricsOnPage` reads it the other way round for the assistant's page
 * routing.
 */
export const METRIC_PAGE_DOMAIN: Readonly<
  Record<MetricValues, PageDomain | null>
> = {
  headcount: 'compositions',
  growth_rate: 'compositions',
  hiring_rate: 'compositions',
  attrition_rate: 'compositions',
  tenure: 'compositions',
  salary: 'compositions',
  share_of_headcount: 'compositions',

  transitions_inflow: 'transitions',
  transitions_outflow: 'transitions',
  transitions_inflows_and_outflows: 'transitions',

  posting_salary: 'postings',
  time_to_fill: 'postings',
  postings_active: 'postings',
  postings_new: 'postings',
  postings_removed: 'postings',
  postings_volume: 'postings',

  base_salary: 'pay',
  total_compensation: 'pay',
  contractor_pay_rate: 'pay',
  // Derived from pay data, charted by reports only.
  base_salary_percentiles: null,
  total_compensation_percentiles: null,
  contractor_pay_rate_percentiles: null,
  total_spend_salary: null,
  total_spend_compensation: null,

  // The picker files `time_to_fill_percentile` under Postings; the page that
  // shows it is Market Conditions, so a chart of it routes there.
  demand_supply: 'market-conditions',
  demand_supply_percentile: 'market-conditions',
  wage_pressure: 'market-conditions',
  wage_pressure_percentile: 'market-conditions',
  market_tightness: 'market-conditions',
  time_to_fill_percentile: 'market-conditions',
  market_share: 'market-conditions',
  target_companies_rec: 'market-conditions',

  sentiment_top_bottom: 'sentiment',
  overall: 'sentiment',
  compensation: 'sentiment',
  benefits: 'sentiment',
  healthcare: 'sentiment',
  retirement_pension: 'sentiment',
  childcare: 'sentiment',
  education_assistance: 'sentiment',
  wellness_programs: 'sentiment',
  commission: 'sentiment',
  career_growth: 'sentiment',
  onboarding: 'sentiment',
  mentorship: 'sentiment',
  promotion_opportunities: 'sentiment',
  training: 'sentiment',
  job_security: 'sentiment',
  prestige: 'sentiment',
  business_outlook: 'sentiment',
  product_and_service_quality: 'sentiment',
  mission_and_vision: 'sentiment',
  clients_and_customers: 'sentiment',
  company_growth: 'sentiment',
  culture: 'sentiment',
  coworkers: 'sentiment',
  workplace_facilities: 'sentiment',
  safety: 'sentiment',
  office_politics: 'sentiment',
  harassment: 'sentiment',
  leadership: 'sentiment',
  independence: 'sentiment',
  diversity_and_inclusion: 'sentiment',
  honesty: 'sentiment',
  ethics: 'sentiment',
  commitment: 'sentiment',
  integrity: 'sentiment',
  work_life_balance: 'sentiment',
  remote_work: 'sentiment',
  paid_time_off: 'sentiment',
  schedule_flexibility: 'sentiment',
  commute: 'sentiment',
  overtime: 'sentiment',
  family_support: 'sentiment',
  technology: 'sentiment',
  transparency: 'sentiment',
  efficiency_and_bureaucracy: 'sentiment',
  internal_communication: 'sentiment',
  compliance: 'sentiment',
  positive_review: 'sentiment',
  negative_review: 'sentiment',

  screener_headcount: 'screener',
  screener_growth: 'screener',
  screener_growth_yoy: 'screener',
  screener_hiring: 'screener',
  screener_attrition: 'screener',
  screener_salary: 'screener',
  screener_tenure: 'screener',
  screener_inflow: 'screener',
  screener_outflow: 'screener',

  subsidiary_count: 'subsidiaries',

  // A summary widget's payload (the Overview pages are redirects now), the
  // Talent Discovery app's count, and the Overview map.
  entity_summary: null,
  td_users: null,
  overview_map: null,
};

/**
 * Market-conditions figures a company keeps although it has no Market
 * Conditions page: its place among its competitors is company-scoped. The
 * rest describe a labour market (demand against supply, wage pressure,
 * tightness), so a company scored through them would be read as if it were a
 * market.
 */
const AVAILABLE_WITHOUT_PAGE: readonly MetricValues[] = [
  'market_share',
  'target_companies_rec',
];

/** The metrics a page shows: which page a chart of them belongs to. */
export const metricsOnPage = (domain: PageDomain): readonly MetricValues[] =>
  (Object.keys(METRIC_PAGE_DOMAIN) as MetricValues[]).filter(
    (metric) => METRIC_PAGE_DOMAIN[metric] === domain
  );

/**
 * Whether an entity's dashboards can chart a metric at all: the entity has the
 * page the metric is shown on. `getAllowedChartTypesForMetric` returns no chart
 * type for a metric this rejects, so the Chart Builder's variable picker and
 * `isValidCombination` follow it, and build-a-dash's entity context and
 * compiler call it directly. The assistant's server reads inherit it by
 * construction (the build-a-dash README's "Capabilities Matrix").
 */
export function isMetricAvailableForEntity(
  metric: MetricValues,
  entityType: EntityType
): boolean {
  // A saved-query variable (`sq:<id>`) is typed as a metric but is not one.
  if (isSavedQueryVariableMetric(metric)) return true;
  const domain = METRIC_PAGE_DOMAIN[metric];
  return (
    domain === null ||
    AVAILABLE_WITHOUT_PAGE.includes(metric) ||
    ENTITY_PAGE_DOMAINS[entityType].includes(domain)
  );
}
